import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { bankIdForQuestionSave, isQuestionType, questionTypeRequiresReview } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { questionSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { QuestionOptionsSection } from './QuestionOptionsSection';
import { QuestionPreviewModal } from './QuestionPreviewModal';
import {
  defaultOptionsForType,
  hasQuestionOptions,
  QUESTION_TYPE_OPTIONS,
  type QuestionOptionDraft,
} from './questionEditor';

interface QuestionForm {
  title: string;
  stem: string;
  type: string;
  bankId: string;
  feedbackCorrect: string;
  feedbackIncorrect: string;
  explanation: string;
}

interface QuestionBank {
  id: string;
  title: string;
}

interface ExistingQuestion extends QuestionForm {
  id?: string;
  requiresReview?: boolean;
  currentVersion?: {
    stem?: string;
    explanation?: string | null;
    feedbackCorrect?: string | null;
    feedbackIncorrect?: string | null;
    options?: { label?: string | null; content: string; isCorrect: boolean }[];
  };
}

export function CreateQuestionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const editId = params.get('id');
  const preselectedBank = params.get('bankId') ?? '';

  const { data: banksData } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => api<{ data: QuestionBank[] }>('/questions/banks'),
  });

  const banks = banksData?.data ?? [];

  const { data: existing, isLoading } = useQuery({
    queryKey: ['questions', editId],
    queryFn: () => api<ExistingQuestion>(`/questions/${editId}`),
    enabled: Boolean(editId),
  });

  const { register, handleSubmit, watch } = useForm<QuestionForm>({
    defaultValues: {
      title: '',
      stem: '',
      type: 'single_choice',
      bankId: preselectedBank,
      feedbackCorrect: '',
      feedbackIncorrect: '',
      explanation: '',
    },
    values: existing
      ? {
          title: existing.title ?? '',
          stem: existing.stem ?? existing.currentVersion?.stem ?? '',
          type: existing.type ?? 'single_choice',
          bankId: existing.bankId ?? '',
          feedbackCorrect: existing.currentVersion?.feedbackCorrect ?? '',
          feedbackIncorrect: existing.currentVersion?.feedbackIncorrect ?? '',
          explanation: existing.currentVersion?.explanation ?? '',
        }
      : undefined,
  });

  const type = watch('type');
  const bankId = watch('bankId');
  const title = watch('title');
  const stem = watch('stem');

  const [requiresReview, setRequiresReview] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [options, setOptions] = useState<QuestionOptionDraft[]>(() => defaultOptionsForType('single_choice'));
  const [editLoaded, setEditLoaded] = useState(false);
  const loadedQuestionId = useRef<string | null>(null);
  const previousType = useRef(type);

  useEffect(() => {
    if (!editId || !existing?.id) return;
    if (loadedQuestionId.current === existing.id) return;

    const loadedType = existing.type ?? 'single_choice';
    const apiOptions = existing.currentVersion?.options ?? [];

    if (apiOptions.length > 0) {
      setOptions(
        apiOptions.map((opt, index) => ({
          label: opt.label ?? String.fromCharCode(65 + index),
          content: opt.content,
          is_correct: Boolean(opt.isCorrect),
        })),
      );
    } else if (hasQuestionOptions(loadedType)) {
      setOptions(defaultOptionsForType(loadedType));
    } else {
      setOptions([]);
    }

    loadedQuestionId.current = existing.id;
    previousType.current = loadedType;
    setRequiresReview(questionTypeRequiresReview(loadedType) || Boolean(existing.requiresReview));
    setEditLoaded(true);
  }, [editId, existing]);

  useEffect(() => {
    if (editId && !editLoaded) return;
    if (previousType.current === type) return;
    setOptions(defaultOptionsForType(type));
    setRequiresReview(questionTypeRequiresReview(type));
    previousType.current = type;
  }, [type, editId, editLoaded]);

  const typeIsAllowed = isQuestionType(type);
  const typeLabel = useMemo(
    () => QUESTION_TYPE_OPTIONS.find((item) => item.value === type)?.label ?? type,
    [type],
  );

  const bankLabel = useMemo(() => {
    if (!bankId) return 'Sin banco';
    return banks.find((b) => b.id === bankId)?.title ?? 'Sin banco';
  }, [bankId, banks]);

  const mutation = useMutation({
    mutationFn: (values: QuestionForm) => {
      const payload = {
        title: values.title,
        stem: values.stem,
        type: values.type,
        bankId: bankIdForQuestionSave(values.bankId),
        feedbackCorrect: values.feedbackCorrect.trim() || undefined,
        feedbackIncorrect: values.feedbackIncorrect.trim() || undefined,
        explanation: values.explanation.trim() || undefined,
        requiresReview: questionTypeRequiresReview(values.type) ? true : requiresReview,
        options: hasQuestionOptions(values.type)
          ? options.map((opt, index) => ({
              label: opt.label,
              content: opt.content,
              isCorrect: opt.is_correct,
              sortOrder: index,
            }))
          : undefined,
      };

      return editId
        ? api(`/questions/${editId}`, { method: 'PATCH', body: payload })
        : api('/questions', { method: 'POST', body: payload });
    },
    onSuccess: () => {
      toast(questionSuccessToast(editId ? 'edit' : 'create'));
      void queryClient.invalidateQueries({ queryKey: ['questions'] });
      void queryClient.invalidateQueries({ queryKey: ['question-banks'] });
      navigate(preselectedBank ? `/preguntas?bankId=${preselectedBank}` : '/preguntas');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, editId ? 'No se pudo guardar la pregunta' : 'No se pudo crear la pregunta'));
    },
  });

  return (
    <div className="lms-question-create lms-site-admin-hub-page font-sans">
      <header className="lms-question-create__header">
        <div className="lms-question-create__heading">
          <HubBackLink to={preselectedBank ? `/preguntas?bankId=${preselectedBank}` : '/preguntas'} />
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {editId ? 'Editar pregunta' : 'Nueva pregunta'}
          </h1>
        </div>
      </header>

      {editId && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit((values) => {
            if (!isQuestionType(values.type)) return;
            mutation.mutate(values);
          })}
          className="lms-question-create__layout"
        >
          <div className="lms-question-create__main">
            <section className="lms-question-create__card">
              <h2 className="lms-question-create__card-title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  info
                </span>
                Información de la Pregunta
              </h2>

              <div className="lms-question-create__grid-2">
                <div className="lms-question-create__field">
                  <label htmlFor="title">
                    Título interno <span className="lms-question-create__required">*</span>
                  </label>
                  <input
                    id="title"
                    type="text"
                    placeholder="Ej. Seguridad en el trabajo"
                    required
                    {...register('title', { required: true })}
                  />
                </div>
                <div className="lms-question-create__field">
                  <label htmlFor="code">Código (Opcional)</label>
                  <input id="code" type="text" name="code" placeholder="Ej. PREG-101" disabled />
                </div>
                <div className="lms-question-create__field">
                  <label htmlFor="type">
                    Tipo <span className="lms-question-create__required">*</span>
                  </label>
                  <div className="lms-question-create__select-wrap">
                    <select id="type" required {...register('type')}>
                      {!typeIsAllowed ? (
                        <option value={type}>
                          Tipo no soportado ({type}) — cámbialo
                        </option>
                      ) : null}
                      {QUESTION_TYPE_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                </div>
                <div className="lms-question-create__field">
                  <label htmlFor="question_bank_id">Banco</label>
                  <div className="lms-question-create__select-wrap">
                    <select id="question_bank_id" {...register('bankId')}>
                      <option value="">Sin banco</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.title}
                        </option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </div>
                </div>
              </div>
              {!typeIsAllowed ? (
                <p className="mt-3 text-sm text-danger-600" role="alert">
                  Esta pregunta usa un tipo que ya no está permitido. Elige uno de la lista antes de
                  guardar.
                </p>
              ) : null}
              {typeIsAllowed ? (
                <label className="lms-question-create__review">
                  <input
                    type="checkbox"
                    checked={questionTypeRequiresReview(type) || requiresReview}
                    disabled={questionTypeRequiresReview(type)}
                    onChange={(event) => setRequiresReview(event.target.checked)}
                  />
                  <span>
                    <strong>Requiere revisión</strong>
                    <p>
                      {questionTypeRequiresReview(type)
                        ? 'Este tipo siempre queda para corrección manual.'
                        : requiresReview
                          ? 'Una persona la revisa. Las respuestas aceptadas quedan solo como referencia.'
                          : 'Se autocorrige al enviar.'}
                    </p>
                  </span>
                </label>
              ) : null}
            </section>

            <section className="lms-question-create__card">
              <h2 className="lms-question-create__card-title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  edit_note
                </span>
                Contenido de la Pregunta
              </h2>
              <div className="lms-question-create__field">
                <label htmlFor="stem">
                  Enunciado <span className="lms-question-create__required">*</span>
                </label>
                <textarea
                  id="stem"
                  rows={6}
                  required
                  placeholder="Escribe el enunciado de la pregunta aquí..."
                  {...register('stem', { required: true })}
                />
              </div>
            </section>

            {hasQuestionOptions(type) && (
              <QuestionOptionsSection type={type} options={options} onChange={setOptions} />
            )}

            <section className="lms-question-create__card">
              <h2 className="lms-question-create__card-title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  forum
                </span>
                Retroalimentación y Explicación
              </h2>

              <div className="lms-question-create__field">
                <label htmlFor="feedback_correct">Retroalimentación correcta</label>
                <textarea
                  id="feedback_correct"
                  rows={3}
                  placeholder="Mensaje para cuando el estudiante responde correctamente..."
                  {...register('feedbackCorrect')}
                />
              </div>
              <div className="lms-question-create__field">
                <label htmlFor="feedback_incorrect">Retroalimentación incorrecta</label>
                <textarea
                  id="feedback_incorrect"
                  rows={3}
                  placeholder="Mensaje para cuando el estudiante responde incorrectamente..."
                  {...register('feedbackIncorrect')}
                />
              </div>
              <div className="lms-question-create__field lms-question-create__field--last">
                <label htmlFor="explanation">Explicación general</label>
                <textarea
                  id="explanation"
                  rows={4}
                  placeholder="Explicación detallada de la respuesta correcta (visible después de responder)..."
                  {...register('explanation')}
                />
              </div>
            </section>
          </div>

          <aside className="lms-question-create__aside">
            <section className="lms-question-create__summary">
              <h3>Resumen de Pregunta</h3>
              <div className="lms-question-create__summary-rows">
                <div>
                  <span>Tipo actual:</span>
                  <strong>{typeLabel}</strong>
                </div>
                <div>
                  <span>Banco:</span>
                  <strong className="lms-question-create__pill">{bankLabel}</strong>
                </div>
                <div>
                  <span>Estado:</span>
                  <strong className="lms-question-create__status">
                    <span className="lms-question-create__status-dot" aria-hidden="true" />
                    Borrador
                  </strong>
                </div>
              </div>
            </section>

            <button
              type="button"
              className="lms-question-create__preview"
              onClick={() => setPreviewOpen(true)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                visibility
              </span>
              Vista previa
            </button>

            <div className="lms-question-create__actions">
              <button
                type="submit"
                className="lms-question-create__submit"
                disabled={mutation.isPending || !typeIsAllowed}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  save
                </span>
                {editId ? 'Guardar cambios' : 'Crear pregunta'}
              </button>
              <Link
                to={preselectedBank ? `/preguntas?bankId=${preselectedBank}` : '/preguntas'}
                className="lms-question-create__cancel"
              >
                Cancelar
              </Link>
            </div>
          </aside>

          <QuestionPreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            title={title}
            stem={stem}
            type={type}
            typeLabel={typeLabel}
            options={options}
          />
        </form>
      )}
    </div>
  );
}
